/**
 * Tâche 1444 (Eric 2026-10-02) — Interrupteur et bandeau de la synchronisation.
 *
 * Quatre décisions, toutes arbitrées par Eric le 2026-10-05 :
 *   1. **Le temps de référence est ton défilement s'il y en a eu un, sinon la
 *      position du lecteur.** Un défilement est enregistré uniquement sur un
 *      geste HUMAIN (molette, doigt, touches de navigation) : le défilement
 *      automatique du lecteur vers le tour actif en est donc exclu, sans quoi
 *      les deux se poursuivraient en boucle.
 *   2. **Quand le temps ne tombe sur aucun tour, on montre le plus proche ET
 *      ON LE DIT.** Le montrer sans le dire ferait croire à un décalage de
 *      contenu entre les deux sources.
 *   3. **Le lecteur vidéo ne suit pas.** La synchronisation sert à comparer
 *      deux textes, pas à déplacer la lecture.
 *   4. **Interrupteur, allumé par défaut**, mémorisé dans le navigateur.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Info } from "@phosphor-icons/react";
import {
  amenerEnHaut, conteneurDe, formaterTemps, ligneCible, lignesHorodatees,
  tempsEnHautDe,
} from "../../lib/syncTranscriptions";
import { useLang } from "../../contexts/LanguageContext";

// Un onglet qui vient d'apparaître n'a pas encore ses lignes : on réessaie
// quelques fois plutôt que de renoncer au premier coup.
const RELANCES_MS = [0, 120, 350, 800, 1600];

// Tâche 1453 — ⚠️ L'INTERRUPTEUR N'EST PLUS ICI : c'est désormais une case à
// cocher posée sous le menu des trois transcriptions (`TranscriptPanel`), sur
// demande d'Eric. Ce composant ne garde que la MÉCANIQUE et le message d'état
// — et il reçoit `actif` en propriété, pour qu'il n'existe qu'UN réglage.
export const SyncCurseursBandeau = ({ sousOnglet, currentTime, nbLignes, actif }) => {
  const { tr } = useLang();
  const [etat, setEtat] = useState(null);
  const tempsDefilementRef = useRef(null);
  const sourceDuTempsRef = useRef("lecteur");
  const ongletPrecedentRef = useRef(sousOnglet);
  const tempsLecteurRef = useRef(0);

  useEffect(() => { tempsLecteurRef.current = currentTime || 0; }, [currentTime]);

  // Éteindre la synchronisation doit effacer le message : le laisser
  // affirmerait un alignement qui ne se fait plus.
  useEffect(() => { if (!actif) setEtat(null); }, [actif]);

  // ── Décision 1 : seul un geste humain enregistre un temps de défilement ──
  useEffect(() => {
    const conteneur = conteneurDe(sousOnglet);
    if (!conteneur) return undefined;
    const TOUCHES = ["PageDown", "PageUp", "Home", "End", "ArrowDown", "ArrowUp"];
    const noter = () => {
      // Lu au tour suivant : au moment du geste, le défilement n'a pas encore
      // eu lieu et on enregistrerait la position d'avant.
      window.requestAnimationFrame(() => {
        const t = tempsEnHautDe(conteneurDe(sousOnglet));
        if (t != null) {
          tempsDefilementRef.current = t;
          sourceDuTempsRef.current = "defilement";
        }
      });
    };
    const surTouche = (e) => { if (TOUCHES.includes(e.key)) noter(); };
    conteneur.addEventListener("wheel", noter, { passive: true });
    conteneur.addEventListener("touchmove", noter, { passive: true });
    conteneur.addEventListener("keydown", surTouche);
    return () => {
      conteneur.removeEventListener("wheel", noter);
      conteneur.removeEventListener("touchmove", noter);
      conteneur.removeEventListener("keydown", surTouche);
    };
  }, [sousOnglet, nbLignes]);

  const appliquer = useCallback((cible, temps) => {
    const conteneur = conteneurDe(cible);
    const lignes = lignesHorodatees(conteneur);
    if (!lignes.length) return false;
    const trouvee = ligneCible(lignes, temps);
    if (!trouvee) return false;
    amenerEnHaut(conteneur, trouvee.el);
    trouvee.el.classList.add("ring-2", "ring-sky-400", "bg-sky-50");
    window.setTimeout(() => {
      trouvee.el.classList.remove("ring-2", "ring-sky-400", "bg-sky-50");
    }, 2600);
    setEtat({
      reference: temps,
      atteint: trouvee.temps,
      contient: trouvee.contient,
      ecart: Math.round(trouvee.ecart),
      position: trouvee.position,
      source: sourceDuTempsRef.current,
    });
    return true;
  }, []);

  // ── Au changement d'onglet, on applique le temps de référence ───────────
  useEffect(() => {
    const precedent = ongletPrecedentRef.current;
    ongletPrecedentRef.current = sousOnglet;
    if (!actif || precedent === sousOnglet) return undefined;
    const temps = sourceDuTempsRef.current === "defilement"
      && tempsDefilementRef.current != null
      ? tempsDefilementRef.current
      : tempsLecteurRef.current;
    if (!Number.isFinite(temps) || temps <= 0) {
      setEtat(null);
      return undefined;
    }
    const minuteries = RELANCES_MS.map((ms) => window.setTimeout(() => {
      appliquer(sousOnglet, temps);
    }, ms));
    return () => minuteries.forEach(window.clearTimeout);
  }, [sousOnglet, actif, appliquer]);

  const memeInstant = etat?.contient && etat.ecart <= 2;

  if (!actif || !etat) return null;

  return (
    <div className="mb-2" data-testid="sync-curseurs">
      <div className="flex flex-wrap items-center gap-2">
        {etat && (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-zinc-600"
                data-testid="sync-curseurs-etat">
            <Info size={12} className="text-zinc-400 flex-shrink-0" />
            {memeInstant ? (
              <span data-testid="sync-curseurs-exact">
                {tr(`Ouvert au même instant : ${formaterTemps(etat.atteint)}`,
                    `Opened at the same moment: ${formaterTemps(etat.atteint)}`)}
              </span>
            ) : etat.position === "avant_le_debut" ? (
              <span className="text-amber-700" data-testid="sync-curseurs-hors-plage">
                {tr(`Cette source ne commence qu'à ${formaterTemps(etat.atteint)} : rien à ${formaterTemps(etat.reference)}. Premier tour affiché.`,
                    `This source only starts at ${formaterTemps(etat.atteint)}: nothing at ${formaterTemps(etat.reference)}. First turn shown.`)}
              </span>
            ) : (
              <span className="text-amber-700" data-testid="sync-curseurs-approche">
                {tr(`${formaterTemps(etat.atteint)} — le tour le plus proche de ${formaterTemps(etat.reference)} (écart ${etat.ecart} s). Cette source n'a pas de tour qui commence à cet instant.`,
                    `${formaterTemps(etat.atteint)} — the closest turn to ${formaterTemps(etat.reference)} (${etat.ecart}s off). This source has no turn starting at that moment.`)}
              </span>
            )}
            <span className="text-zinc-400">
              {etat.source === "defilement"
                ? tr("· d'après ton défilement", "· from your scrolling")
                : tr("· d'après la position du lecteur", "· from the player position")}
            </span>
          </span>
        )}
      </div>
    </div>
  );
};

export default SyncCurseursBandeau;
